import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FileSearch, ArrowLeft, Check, Loader2, MessageSquareWarning, Clock, CheckCircle2 } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { PrdReviewDetail, PrdReviewList, PrdReviewStatus, PrdReviewSummary } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel, PageHeader } from './dashboards/ui';

// The Expert's workbench: every drafted PRD with its review state. Approving
// here is what clears the build gate; requesting changes blocks it with the
// comment until re-approved.

const STATUS_META: Record<PrdReviewStatus, { label: string; cls: string; icon: React.ElementType }> = {
  pending: { label: 'Awaiting review', cls: 'bg-amber-50 text-amber-700', icon: Clock },
  changes_requested: { label: 'Changes requested', cls: 'bg-red-50 text-red-600', icon: MessageSquareWarning },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
};

const StatusPill: React.FC<{ status: PrdReviewStatus }> = ({ status }) => {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.cls}`}>
      <Icon size={12} />{meta.label}
    </span>
  );
};

const PrdReviewsPage: React.FC = () => {
  const [list, setList] = useState<PrdReviewList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<PrdReviewDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [actErr, setActErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await aaiApi.listPrdReviews();
      setList(data); setError(null);
    } catch { setError('Could not load the review queue.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openReview = async (r: PrdReviewSummary) => {
    setBusy(true); setActErr(null); setComment('');
    try {
      const { data } = await aaiApi.getPrdReview(r.engagement_id);
      setOpen(data);
    } catch { setError('Could not open this PRD.'); }
    finally { setBusy(false); }
  };

  const decide = async (decision: 'approve' | 'request_changes') => {
    if (!open) return;
    if (decision === 'request_changes' && !comment.trim()) {
      setActErr('Say what needs to change — the comment is the review.');
      return;
    }
    setBusy(true); setActErr(null);
    try {
      await aaiApi.decidePrdReview(open.engagement_id, { decision, comment: comment.trim() || undefined });
      setOpen(null); await load();
    } catch (e) {
      setActErr((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Could not save your decision.');
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Loading the review queue…" />;
  if (error || !list) return <ErrorNote message={error || 'Something went wrong.'} />;

  // ── Detail view ────────────────────────────────────────────────────────
  if (open) {
    return (
      <div className="max-w-3xl mx-auto">
        <button onClick={() => setOpen(null)} className="inline-flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeft size={15} /> Back to the queue
        </button>
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="text-2xl font-semibold text-gray-900">{open.title}</h1>
          <StatusPill status={open.review.status} />
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {open.request_title ? `From the request “${open.request_title}” by ${open.requester_email}. ` : ''}
          {open.drafted_at ? `Drafted ${new Date(open.drafted_at).toLocaleDateString()}.` : ''}
        </p>

        {open.review.status === 'changes_requested' && open.review.comment && (
          <Card className="mb-4 border-red-100 bg-red-50/50">
            <MonoLabel className="mb-1">Changes requested by {open.review.reviewer_email}</MonoLabel>
            <p className="text-[13px] text-gray-700">{open.review.comment}</p>
          </Card>
        )}

        <Card className="mb-4">
          <MonoLabel className="mb-3">PRD · {open.section_count} sections</MonoLabel>
          {open.prd.markdown ? (
            <div className="prose prose-sm max-w-none text-[13.5px] leading-relaxed [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm">
              <ReactMarkdown>{open.prd.markdown}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-[13px] text-gray-400">No PRD body available.</p>
          )}
        </Card>

        {open.review.history.length > 0 && (
          <Card className="mb-4">
            <MonoLabel className="mb-2">Review history</MonoLabel>
            <div className="space-y-1.5">
              {open.review.history.map((h, i) => (
                <div key={i} className="text-[12.5px] text-gray-600">
                  <span className="font-semibold">{h.reviewer_email}</span>
                  {' '}{h.status === 'approved' ? 'approved' : 'requested changes'}
                  {h.decided_at ? ` · ${new Date(h.decided_at).toLocaleString()}` : ''}
                  {h.comment ? <span className="block text-gray-500">“{h.comment}”</span> : null}
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <MonoLabel className="mb-2">Your review</MonoLabel>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            placeholder="What needs to change? (required when requesting changes, optional when approving)"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200 mb-3" />
          {actErr && <p className="text-[12px] text-red-600 mb-2">{actErr}</p>}
          <div className="flex items-center gap-2 justify-end">
            <button disabled={busy} onClick={() => decide('request_changes')}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-4 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 whitespace-nowrap">
              <MessageSquareWarning size={14} /> Request changes
            </button>
            <button disabled={busy} onClick={() => decide('approve')}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 whitespace-nowrap">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Approve for build
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Queue ──────────────────────────────────────────────────────────────
  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="PRD reviews"
        subtitle="Every drafted spec waits for your engineering pass before it can be built. Approve it, or send it back with what needs to change."
      />
      <div className="flex items-center gap-4 mb-4 text-[12.5px] text-gray-500">
        <span><b className="text-amber-600">{list.counts.pending}</b> awaiting</span>
        <span><b className="text-red-500">{list.counts.changes_requested}</b> changes requested</span>
        <span><b className="text-emerald-600">{list.counts.approved}</b> approved</span>
      </div>
      {list.reviews.length === 0 ? (
        <Card><p className="text-[13px] text-gray-400 text-center py-6">No PRDs drafted yet — they appear here the moment one is ready.</p></Card>
      ) : (
        <div className="space-y-2.5">
          {list.reviews.map((r) => (
            <Card key={r.engagement_id} className="flex items-center gap-3.5">
              <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 grid place-items-center shrink-0"><FileSearch size={17} /></span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[14px] text-gray-900 truncate">{r.title}</div>
                <div className="text-[12px] text-gray-500 truncate">
                  {r.section_count} sections
                  {r.request_title ? ` · from “${r.request_title}”` : ''}
                  {r.drafted_at ? ` · drafted ${new Date(r.drafted_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              <StatusPill status={r.review.status} />
              <button disabled={busy} onClick={() => openReview(r)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-semibold px-3.5 py-1.5 whitespace-nowrap">
                {r.review.status === 'pending' ? 'Review' : 'Open'}
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PrdReviewsPage;
